import React, { useMemo } from 'react';
import useGradesTableStore from '@/components/student/AssignmentsBasedGradesTable/store/useGradesTableStore';
import { TABLE_ROW_CLASS } from '@/components/student/AssignmentsBasedGradesTable/utils';
import { ISubmission } from '@/components/interfaces';
import cn from '@/utils/cn';

const TotalPointsRow = () => {
  const { assignments, submissions, unit } = useGradesTableStore(state => ({
    assignments: state.assignments,
    submissions: state.submissions,
    unit: state.unit,
  }));

  const lessonOrUnitLabel = unit ? 'Unit' : 'Lesson';

  const rowClassName = cn(
    TABLE_ROW_CLASS,
    'tw-border-y tw-border-solid tw-border-grey tw-bg-light-grey tw-align-middle tw-p-4',
  );

  const submissionHasScore = (submission: ISubmission) => (
    typeof submission.total_score_override === 'number' || typeof submission.total_score === 'number'
  );

  // Calculate total student points and max available assignment points
  const { studentPoints, maxPoints } = useMemo(() => submissions?.reduce(
    (totals, submission) => {
      if (!submissionHasScore(submission)) return totals;
      const assignment = assignments.find(a => a.id === submission.assignment_id);
      const assignmentMaxPoints = assignment ? assignment.max_points || 0 : 0;

      let score = 0;
      if (assignment) {
        if (assignment.assignment_details.type === 'Assessment') {
          if (submission.graded_at) {
            score = submission.total_score_override ?? submission.total_score ?? 0;
          }
        } else {
          score = submission.total_score_override ?? submission.total_score ?? 0;
        }
      }

      return {
        maxPoints: totals.maxPoints + assignmentMaxPoints,
        studentPoints: totals.studentPoints + score,
      };
    },
    { maxPoints: 0, studentPoints: 0 },
  ) || { maxPoints: 0, studentPoints: 0 }, [assignments, submissions]);

  const scorePercentage = maxPoints > 0 ? Math.round((studentPoints / maxPoints) * 100) : 0;

  return (
    <tr className="tw-h-[3rem] tw-text-base tw-my-4">
      <td className={cn(rowClassName, 'tw-pl-4 tw-rounded-l-lg tw-border-l tw-font-bold')}>
        {lessonOrUnitLabel} Total
      </td>
      <td className={rowClassName} />
      <td className={rowClassName} />
      <td className={rowClassName} />
      <td className={rowClassName}>
        {studentPoints}/{maxPoints}
      </td>
      <td className={cn(rowClassName, 'tw-rounded-r-lg tw-border-r')}>
        {scorePercentage}%
      </td>
    </tr>
  );
};

export default TotalPointsRow;
